import { useState, useEffect } from 'react'
import {
  Box, Typography, Card, CardContent, Switch, Chip,
  CircularProgress, Tooltip, Stack, Button, IconButton,
  Divider, Select, MenuItem, TextField, FormControl, InputLabel,
} from '@mui/material'
import {
  AccountTree as WorkflowIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { PageHeader } from '../components/shared/PageHeader'
import { PageActionButtons } from '../components/shared/PageActionButtons'
import { themeColors } from '../app/layout/theme'
import {
  workflowsApi,
  WorkflowRule,
  TRIGGER_EVENTS,
  TRIGGER_LABELS,
  CONDITION_OPERATORS,
  NO_VALUE_OPERATORS,
  OPERATOR_SYMBOL,
  ACTION_TYPES,
  ACTION_DEFAULT_PARAMS,
  CreateWorkflowPayload,
  UpdateWorkflowPayload,
} from '../lib/api/workflowsApi'
import { FormInput, FormSelect, FormDialog } from '../components/ui'
import { authStore } from '../lib/stores/authStore'
import { usePermission } from '../lib/hooks/usePermission'
import { useOrgSelector } from '../context/OrgSelectorContext'

// ── Local row types ───────────────────────────────────────────────────────────

interface ConditionRow { field: string; operator: string; value: string }
interface ActionRow    { actionType: string; paramsJson: string }

const DEFAULT_CONDITION: ConditionRow = { field: '', operator: 'EQUALS', value: '' }
const DEFAULT_ACTION:    ActionRow    = { actionType: 'AUTO_ASSIGN', paramsJson: '{"strategy":"ROUND_ROBIN"}' }

// ── WorkflowCard ──────────────────────────────────────────────────────────────

function WorkflowCard({
  rule,
  onToggle,
  onDelete,
  onEdit,
  canToggle,
  canDelete,
  canEdit,
}: {
  rule:      WorkflowRule
  onToggle:  (id: string, active: boolean) => void
  onDelete:  (id: string) => void
  onEdit:    (rule: WorkflowRule) => void
  canToggle: boolean
  canDelete: boolean
  canEdit:   boolean
}) {
  return (
    <Card sx={{ backgroundColor: themeColors.surface, border: `1px solid ${themeColors.border}` }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '15px', color: themeColors.text.primary }}>
              {rule.name}
            </Typography>
            <Chip
              label={TRIGGER_LABELS[rule.triggerEvent] ?? rule.triggerEvent}
              size="small"
              sx={{ mt: 0.5, fontSize: '11px', backgroundColor: '#e3f2fd', color: '#1565c0' }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {canToggle && (
              <Tooltip title={rule.isActive ? 'Disable' : 'Enable'}>
                <Switch
                  size="small"
                  checked={rule.isActive}
                  onChange={(e) => onToggle(rule.id, e.target.checked)}
                  color="primary"
                />
              </Tooltip>
            )}
            {canEdit && (
              <Tooltip title="Edit workflow">
                <IconButton size="small" onClick={() => onEdit(rule)} sx={{ color: '#1976d2' }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete workflow">
                <IconButton size="small" onClick={() => onDelete(rule.id)} sx={{ color: '#f44336' }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          {/* Conditions */}
          <Box>
            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: themeColors.text.secondary, mb: 0.5 }}>
              CONDITIONS{rule.conditions.length > 1 ? ` (${rule.conditionLogic})` : ''}
            </Typography>
            {rule.conditions.length === 0 ? (
              <Typography sx={{ fontSize: '11px', color: '#aaa', fontStyle: 'italic' }}>
                Match all
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {rule.conditions.map((c) => (
                  <Chip
                    key={c.id}
                    label={`${c.field} ${OPERATOR_SYMBOL[c.operator] ?? c.operator}${c.value ? ` ${c.value}` : ''}`}
                    size="small"
                    sx={{
                      fontSize: '10px', height: 22,
                      backgroundColor: '#f3e5f5', color: '#7b1fa2',
                      justifyContent: 'flex-start', maxWidth: '100%',
                      '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                    }}
                  />
                ))}
              </Stack>
            )}
          </Box>

          {/* Actions */}
          <Box>
            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: themeColors.text.secondary, mb: 0.5 }}>
              ACTIONS
            </Typography>
            {rule.actions.length === 0 ? (
              <Typography sx={{ fontSize: '11px', color: '#aaa', fontStyle: 'italic' }}>
                No actions
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {rule.actions.map((a) => (
                  <Chip
                    key={a.id}
                    label={ACTION_TYPES.find((t) => t.value === a.actionType)?.label ?? a.actionType}
                    size="small"
                    sx={{
                      fontSize: '10px', height: 22,
                      backgroundColor: '#e8f5e9', color: '#2e7d32',
                      justifyContent: 'flex-start',
                    }}
                  />
                ))}
              </Stack>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

// ── Condition row inside the form dialog ──────────────────────────────────────

function ConditionRowEditor({
  cond,
  onChange,
  onRemove,
}: {
  cond:     ConditionRow
  onChange: (updated: ConditionRow) => void
  onRemove: () => void
}) {
  const noValue = NO_VALUE_OPERATORS.has(cond.operator)

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
      <TextField
        size="small"
        label="Field"
        value={cond.field}
        onChange={(e) => onChange({ ...cond, field: e.target.value })}
        placeholder="e.g. source"
        sx={{ flex: 1.2 }}
      />
      <FormControl size="small" sx={{ flex: 1.4 }}>
        <InputLabel>Operator</InputLabel>
        <Select
          label="Operator"
          value={cond.operator}
          onChange={(e) => onChange({ ...cond, operator: e.target.value, value: '' })}
        >
          {CONDITION_OPERATORS.map((o) => (
            <MenuItem key={o.value} value={o.value} sx={{ fontSize: '13px' }}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {!noValue && (
        <TextField
          size="small"
          label="Value"
          value={cond.value}
          onChange={(e) => onChange({ ...cond, value: e.target.value })}
          placeholder="e.g. FACEBOOK"
          sx={{ flex: 1.4 }}
        />
      )}
      <Tooltip title="Remove condition">
        <IconButton onClick={onRemove} size="small" sx={{ mt: 0.5, color: '#f44336', flexShrink: 0 }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

// ── Action row inside the form dialog ────────────────────────────────────────

function ActionRowEditor({
  action,
  onChange,
  onRemove,
}: {
  action:   ActionRow
  onChange: (updated: ActionRow) => void
  onRemove: () => void
}) {
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
      <FormControl size="small" sx={{ flex: 1 }}>
        <InputLabel>Action Type</InputLabel>
        <Select
          label="Action Type"
          value={action.actionType}
          onChange={(e) => {
            const type = e.target.value
            onChange({ actionType: type, paramsJson: ACTION_DEFAULT_PARAMS[type] ?? '{}' })
          }}
        >
          {ACTION_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value} sx={{ fontSize: '13px' }}>
              {t.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        size="small"
        label="Params (JSON)"
        value={action.paramsJson}
        onChange={(e) => onChange({ ...action, paramsJson: e.target.value })}
        multiline
        minRows={1}
        maxRows={4}
        sx={{ flex: 1.5 }}
        inputProps={{ style: { fontFamily: 'monospace', fontSize: '12px' } }}
      />
      <Tooltip title="Remove action">
        <IconButton onClick={onRemove} size="small" sx={{ mt: 0.5, color: '#f44336', flexShrink: 0 }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function WorkflowsPage() {
  const queryClient  = useQueryClient()

  // ── Org selector (superadmin) ──
  const { isSuperAdmin, orgs, selectedOrgId } = useOrgSelector()

  // ── Permissions ──
  const { hasAction } = usePermission()
  const canCreate    = hasAction('/workflows', 'create')
  const canToggle    = hasAction('/workflows', 'update')
  const canEdit      = hasAction('/workflows', 'update')
  const canDelete    = hasAction('/workflows', 'delete')

  // ── Form state ──
  const [showForm, setShowForm]             = useState(false)
  const [editingRule, setEditingRule]       = useState<WorkflowRule | null>(null)
  const [formOrgId, setFormOrgId]           = useState<string>('')
  const [name, setName]                     = useState('')
  const [trigger, setTrigger]               = useState('')
  const [conditionLogic, setConditionLogic] = useState<'AND' | 'OR'>('AND')
  const [conditions, setConditions]         = useState<ConditionRow[]>([{ ...DEFAULT_CONDITION }])
  const [actions, setActions]               = useState<ActionRow[]>([{ ...DEFAULT_ACTION }])

  const resetForm = () => {
    setName('')
    setTrigger('')
    setConditionLogic('AND')
    setConditions([{ ...DEFAULT_CONDITION }])
    setActions([{ ...DEFAULT_ACTION }])
    setEditingRule(null)
    setFormOrgId(selectedOrgId ?? orgs[0]?.id ?? '')
  }

  const openNew = () => {
    resetForm()
    setShowForm(true)
  }

  const openEdit = (rule: WorkflowRule) => {
    setEditingRule(rule)
    setName(rule.name)
    setTrigger(rule.triggerEvent)
    setConditionLogic((rule.conditionLogic as 'AND' | 'OR') ?? 'AND')
    // For edit: use the globally selected org (the rule belongs to the currently viewed org)
    setFormOrgId(selectedOrgId ?? orgs[0]?.id ?? '')
    setConditions(
      rule.conditions.length > 0
        ? rule.conditions
            .slice()
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((c) => ({ field: c.field, operator: c.operator, value: c.value ?? '' }))
        : [{ ...DEFAULT_CONDITION }]
    )
    setActions(
      rule.actions.length > 0
        ? rule.actions
            .slice()
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((a) => ({ actionType: a.actionType, paramsJson: a.paramsJson }))
        : [{ ...DEFAULT_ACTION }]
    )
    setShowForm(true)
  }

  // If the dialog is already open and orgs finish loading (or selectedOrgId changes),
  // auto-fill formOrgId so the dropdown isn't left blank.
  useEffect(() => {
    if (showForm && isSuperAdmin && !formOrgId) {
      setFormOrgId(selectedOrgId ?? orgs[0]?.id ?? '')
    }
  }, [showForm, isSuperAdmin, orgs, selectedOrgId, formOrgId])

  // ── Queries ──
  const { data: rules = [], isLoading } = useQuery({
    queryKey: ['workflows'],
    queryFn:  () => workflowsApi.getAll(),
  })

  // ── Mutations ──
  const createMutation = useMutation({
    mutationFn: (payload: CreateWorkflowPayload) => workflowsApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
      toast.success('Workflow created')
      setShowForm(false)
      resetForm()
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err.message || 'Failed to create workflow'),
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      workflowsApi.toggle(id, isActive),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workflows'] }),
    onError: () => toast.error('Failed to update workflow'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => workflowsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
      toast.success('Workflow deleted')
    },
    onError: () => toast.error('Failed to delete workflow'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateWorkflowPayload }) =>
      workflowsApi.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
      toast.success('Workflow updated')
      setShowForm(false)
      resetForm()
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err.message || 'Failed to update workflow'),
  })

  // ── Condition helpers ──
  const updateCondition = (i: number, updated: ConditionRow) =>
    setConditions((prev) => prev.map((c, idx) => (idx === i ? updated : c)))
  const removeCondition = (i: number) =>
    setConditions((prev) => prev.filter((_, idx) => idx !== i))
  const addCondition = () =>
    setConditions((prev) => [...prev, { ...DEFAULT_CONDITION }])

  // ── Action helpers ──
  const updateAction = (i: number, updated: ActionRow) =>
    setActions((prev) => prev.map((a, idx) => (idx === i ? updated : a)))
  const removeAction = (i: number) =>
    setActions((prev) => prev.filter((_, idx) => idx !== i))
  const addAction = () =>
    setActions((prev) => [...prev, { ...DEFAULT_ACTION }])

  // ── Submit ──
  const handleSubmit = () => {
    if (!name.trim())                          return toast.error('Workflow name is required')
    if (!trigger)                              return toast.error('Trigger event is required')
    if (isSuperAdmin && !formOrgId)            return toast.error('Please select an organisation')

    const validConditions = conditions.filter((c) => c.field.trim() && c.operator)
    const validActions    = actions.filter((a) => a.actionType)

    if (validActions.length === 0) return toast.error('At least one action is required')

    const conditionDtos = validConditions.map((c, i) => ({
      field:     c.field.trim(),
      operator:  c.operator,
      value:     NO_VALUE_OPERATORS.has(c.operator) ? undefined : (c.value.trim() || undefined),
      sortOrder: i,
    }))
    const actionDtos = validActions.map((a, i) => ({
      actionType: a.actionType,
      paramsJson: a.paramsJson.trim() || '{}',
      sortOrder:  i,
    }))

    if (editingRule) {
      updateMutation.mutate({
        id: editingRule.id,
        payload: {
          name:           name.trim(),
          triggerEvent:   trigger,
          conditionLogic,
          conditions:     conditionDtos,
          actions:        actionDtos,
          // SuperAdmin explicitly selects the target org; normal users omit this field
          ...(isSuperAdmin && formOrgId ? { orgId: formOrgId } : {}),
        },
      })
    } else {
      createMutation.mutate({
        name:           name.trim(),
        triggerEvent:   trigger,
        conditionLogic,
        conditions:     conditionDtos,
        actions:        actionDtos,
        // SuperAdmin explicitly selects the target org; normal users omit this field
        ...(isSuperAdmin && formOrgId ? { orgId: formOrgId } : {}),
      })
    }
  }

  // ── Render ──
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Workflows"
        breadcrumb={[{ label: 'CRM', to: '/crm' }]}
        actions={canCreate ? (
          <PageActionButtons
            onNew={openNew}
            newLabel="New Workflow"
          />
        ) : undefined}
      />

      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : rules.length === 0 ? (
          <Box sx={{ textAlign: 'center', pt: 8 }}>
            <WorkflowIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
            <Typography sx={{ color: themeColors.text.secondary, fontSize: '14px' }}>
              No workflows yet. Create one to automate lead actions.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            {rules.map((rule) => (
              <WorkflowCard
                key={rule.id}
                rule={rule}
                onToggle={(id, isActive) => toggleMutation.mutate({ id, isActive })}
                onDelete={(id) => deleteMutation.mutate(id)}
                onEdit={openEdit}
                canToggle={canToggle}
                canDelete={canDelete}
                canEdit={canEdit}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* ── Create / Edit Workflow Dialog ── */}
      <FormDialog
        open={showForm}
        onClose={() => { setShowForm(false); resetForm() }}
        onSave={handleSubmit}
        title={editingRule ? 'Edit Workflow' : 'New Workflow'}
        saveLabel={editingRule ? 'Save Changes' : 'Create Workflow'}
        saving={createMutation.isPending || updateMutation.isPending}
        maxWidth="md"
      >
        <Stack spacing={2.5}>
          {/* Basic info */}
          <FormInput
            label="Workflow Name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            placeholder="e.g. Auto-assign Facebook leads"
          />

          {/* Organisation selector — superadmin only */}
          {isSuperAdmin && (
            <FormSelect
              label="Organisation"
              required
              value={formOrgId}
              onChange={(v) => setFormOrgId(v as string)}
              options={orgs.map((o) => ({ label: o.name, value: o.id }))}
              fullWidth
            />
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <FormSelect
              label="Trigger Event"
              required
              value={trigger}
              onChange={(v) => setTrigger(v as string)}
              options={TRIGGER_EVENTS.map((o) => ({ label: TRIGGER_LABELS[o] ?? o, value: o }))}
              fullWidth
            />
            <FormSelect
              label="Condition Logic"
              value={conditionLogic}
              onChange={(v) => setConditionLogic(v as 'AND' | 'OR')}
              options={[
                { label: 'AND — all conditions must match', value: 'AND' },
                { label: 'OR — any condition must match',   value: 'OR'  },
              ]}
              fullWidth
            />
          </Box>

          <Divider />

          {/* Conditions */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary }}>
                Conditions
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addCondition}
                sx={{ fontSize: '12px', textTransform: 'none' }}
              >
                Add Condition
              </Button>
            </Box>

            {conditions.length === 0 ? (
              <Typography sx={{ fontSize: '12px', color: '#aaa', fontStyle: 'italic' }}>
                No conditions — this workflow will run on every {(TRIGGER_LABELS[trigger] ?? trigger) || 'trigger'} event.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {conditions.map((cond, i) => (
                  <ConditionRowEditor
                    key={i}
                    cond={cond}
                    onChange={(updated) => updateCondition(i, updated)}
                    onRemove={() => removeCondition(i)}
                  />
                ))}
              </Stack>
            )}
          </Box>

          <Divider />

          {/* Actions */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary }}>
                Actions <span style={{ color: '#f44336', fontSize: '11px' }}>*</span>
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addAction}
                sx={{ fontSize: '12px', textTransform: 'none' }}
              >
                Add Action
              </Button>
            </Box>

            <Stack spacing={1.5}>
              {actions.map((action, i) => (
                <ActionRowEditor
                  key={i}
                  action={action}
                  onChange={(updated) => updateAction(i, updated)}
                  onRemove={() => removeAction(i)}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </FormDialog>
    </Box>
  )
}
